import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, UserCheck, UserX, FileText, Bell, AlertTriangle, ArrowRight } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '@/lib/api';
import { cacheDashboardStats, getCachedDashboardStats } from '@/lib/syncService';

const statConfig = [
  { key: 'total_clients', icon: Users, color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { key: 'active_clients', icon: UserCheck, color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  { key: 'inactive_clients', icon: UserX, color: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  { key: 'total_documents', icon: FileText, color: 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
  { key: 'pending_reminders', icon: Bell, color: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
];

const CHART_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'];

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function actionLabel(action) {
  const map = {
    login: 'Logged in', created_client: 'Created client', updated_client: 'Updated client',
    deleted_client: 'Deleted client', created_user: 'Created user', uploaded_document: 'Uploaded document',
    created_reminder: 'Created reminder', updated_settings: 'Updated settings', created_category: 'Created category',
    bulk_deleted_clients: 'Bulk deleted', deleted_document: 'Deleted document', updated_user: 'Updated user',
    deleted_user: 'Deleted user', updated_reminder: 'Updated reminder',
  };
  return map[action] || action;
}

export default function DashboardPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, chartsRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/charts'),
        ]);
        setStats(statsRes.data);
        setCharts(chartsRes.data);
        cacheDashboardStats(statsRes.data);
      } catch {
        const cached = await getCachedDashboardStats();
        if (cached) setStats(cached);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('dashboard')}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Trust Account Management Overview</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {statConfig.map((s) => {
          const Icon = s.icon;
          const val = stats?.[s.key] ?? 0;
          return (
            <Card key={s.key} className="card-hover" data-testid={`stat-${s.key}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${s.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-semibold tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {val}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">{t(s.key)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <Card data-testid="chart-category">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Clients by Category
            </h2>
            {charts?.by_category?.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={180}>
                  <PieChart>
                    <Pie
                      data={charts.by_category}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      dataKey="value"
                      stroke="none"
                    >
                      {charts.by_category.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {charts.by_category.map((item, i) => (
                    <div key={item.name} className="flex items-center gap-2 text-sm">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span className="truncate text-muted-foreground">{item.name}</span>
                      <span className="ml-auto font-medium tabular-nums">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-12">No category data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Monthly Client Creation */}
        <Card data-testid="chart-monthly">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              Client Registrations (6 months)
            </h2>
            {charts?.by_month?.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={charts.by_month} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="clients" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-12">No monthly data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Compliance Deadlines */}
      <Card data-testid="compliance-deadlines">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t('compliance_reminders')}
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/clients')} className="text-xs">
              View all <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
          {charts?.upcoming_deadlines?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Trust ID</TableHead>
                  <TableHead className="text-xs">Client</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs">Due Date</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {charts.upcoming_deadlines.map((d, i) => (
                  <TableRow key={i} className="cursor-pointer" onClick={() => navigate(`/clients/${d.client_id}`)}>
                    <TableCell className="font-mono text-xs tabular-nums">{d.trust_id}</TableCell>
                    <TableCell className="text-sm font-medium">{d.client_name || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{d.type}</Badge>
                    </TableCell>
                    <TableCell className="text-xs tabular-nums">
                      {d.due_date ? new Date(d.due_date).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      {d.overdue ? (
                        <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-xs hover:bg-red-100">
                          <AlertTriangle className="h-3 w-3 mr-1" /> Overdue
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs hover:bg-emerald-100">
                          On track
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No compliance deadlines set. Add due dates to client records.</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card data-testid="recent-activity-card">
        <CardContent className="p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            {t('recent_activity')}
          </h2>
          {stats?.recent_activity?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_activity.map((log, i) => (
                <div key={log.id || i} className="flex items-center gap-3 py-2 border-b last:border-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-bold text-muted-foreground">
                      {(log.user_name || 'S').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium">{log.user_name || 'System'}</span>
                      {' '}
                      <span className="text-muted-foreground">{actionLabel(log.action)}</span>
                      {log.entity_name && (
                        <span className="font-medium"> {log.entity_name}</span>
                      )}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{formatTime(log.created_at)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">{t('no_data')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
